import { useOtcAPIService } from '@/composables/services/useOtcAPIService';
import { useTransactions } from '@/composables/useTransactions';
import { roundToPrecision } from '@/helpers/utils';
import { addSentryBreadcrumb } from '@/logs/sentry';
import type { BankTransferRecipient } from '@/references/axios/bankTransfer/types';
import type { Fees, Quote } from '@/references/axios/transactions/types';
import { HHError } from '@/references/HHError';
import { getNetwork } from '@/references/network';
import type { Token } from '@/references/tokens';

export type OtcData = {
  toAmount: string;
  rate: string;
  fromAmount: string;
  fromToken: Token;
};

async function getToEurAmount(
  token: Token,
  tokenAmount: string,
  signal?: AbortSignal
): Promise<OtcData> {
  const amount = roundToPrecision(tokenAmount, token.decimals);
  const service = useOtcAPIService();

  addSentryBreadcrumb({
    level: 'info',
    message: 'ready to get token amount (from eur)',
    data: {
      ramAmount: tokenAmount,
      amount: amount,
      inputToken: token
    }
  });

  const response = await service.toEur(
    {
      amount: amount,
      inputToken: token
    },
    { signal }
  );

  addSentryBreadcrumb({
    level: 'info',
    message: 'fromToken method result ready: ',
    data: response
  });

  return {
    fromAmount: response.inputAmount,
    fromToken: response.inputToken,
    rate: response.rate,
    toAmount: response.outputAmount
  };
}

async function generateQuote(
  otcData: OtcData,
  fees: Fees,
  recipient: BankTransferRecipient,
  signal?: AbortSignal
): Promise<Quote> {
  addSentryBreadcrumb({
    level: 'info',
    message: 'ready to get quote',
    data: {
      otcData: otcData,
      recipient: recipient
    }
  });

  const service = useOtcAPIService();

  const networkInfo = getNetwork(otcData.fromToken.network);

  if (networkInfo === undefined) {
    throw new HHError('fail to load network info from config');
  }

  const response = await service.newQuote(otcData, networkInfo.chainId, recipient.id, fees, {
    signal
  });

  addSentryBreadcrumb({
    level: 'info',
    message: 'got quote',
    data: response
  });

  useTransactions().load();

  return response;
}

async function loadFreshQuote(quoteId: string, signal?: AbortSignal): Promise<Quote> {
  addSentryBreadcrumb({
    level: 'info',
    message: 'loading fresh quote by id',
    data: {
      quoteId: quoteId
    }
  });

  return useOtcAPIService().getQuoteById(quoteId, { signal: signal });
}

async function getFees(beneficiaryId: number, signal?: AbortSignal): Promise<Fees> {
  addSentryBreadcrumb({
    level: 'info',
    message: 'loading fees',
    data: { beneficiaryId }
  });

  const response = await useOtcAPIService().getFees(beneficiaryId, { signal });

  addSentryBreadcrumb({
    level: 'info',
    message: 'received fees',
    data: response
  });

  return response;
}

export const useOtc = () => {
  return {
    getToEurAmount,
    generateQuote,
    loadFreshQuote,
    getFees
  };
};
